import { useEffect } from 'react';
import type { QueryFilter } from '@domain/schemas/filters.schema';
import { applyQueryFilters, areFiltersEqual, getQueryFilters, resetQueryFilters } from '@features/query-builder';
import type { SavedView } from '@queries/fetchSavedViews';
import { invalidateSavedViewCounts, savedViewQueries } from '@queries/fetchSavedViews';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';

import { SAVED_VIEWS_ENTITY } from '../constants';

import { useActiveSavedView } from './useActiveSavedView';

export const useAppliedViewIdStore = create<{ appliedViewId: string | null }>(() => ({ appliedViewId: null }));

export const applySavedViewFilters = (queryClient: QueryClient, view: SavedView) => {
  applyQueryFilters(SAVED_VIEWS_ENTITY, view.filters);
  useAppliedViewIdStore.setState({ appliedViewId: view.id });
  invalidateSavedViewCounts(queryClient);
};

const hasUnsavedEdits = (queryClient: QueryClient, viewId: string): boolean => {
  const views = queryClient.getQueryData(savedViewQueries.list().queryKey);
  const savedFilters = views?.find((v) => v.id === viewId)?.filters;
  return savedFilters !== undefined && !areFiltersEqual(getQueryFilters(SAVED_VIEWS_ENTITY), savedFilters);
};

const resetFiltersOnDeselect = (queryClient: QueryClient, defaultFilters?: QueryFilter[]) => {
  const { appliedViewId } = useAppliedViewIdStore.getState();
  useAppliedViewIdStore.setState({ appliedViewId: null });
  if (appliedViewId === null || hasUnsavedEdits(queryClient, appliedViewId)) return;
  resetQueryFilters(SAVED_VIEWS_ENTITY, defaultFilters);
};

export const useApplySavedViewFilters = (defaultFilters?: QueryFilter[]) => {
  const activeView = useActiveSavedView();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (activeView) {
      // Skip when already applied: a remount (browser back) must not clobber in-progress edits with the baseline.
      if (activeView.id !== useAppliedViewIdStore.getState().appliedViewId) {
        applySavedViewFilters(queryClient, activeView);
      }
    } else resetFiltersOnDeselect(queryClient, defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the id: list refetches swap the object without changing the selection
  }, [activeView?.id, queryClient]);
};
